/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }
}

if (!window.customElements.get('cart-drawer')) {
  window.customElements.define('cart-drawer', CartDrawer);
}

/* Crazy House Free Returns - TEST THEME ONLY
 * Additive cart-drawer enhancement. Existing Symmetry cart code above is unchanged.
 */
(() => {
  if (window.__chReturnReserveLoaded) return;
  window.__chReturnReserveLoaded = true;

  const RETURN_SKU = 'CHRETURNS';
  const RETURN_HANDLE = 'crazy-house-free-returns-test';
  const DEFAULT_MIN_FEE_CENTS = 200;
  const COWBOY_HAT_MIN_FEE_CENTS = 1000;
  const MAX_FEE_CENTS = 2000;
  const BASE_RATE = 0.03;
  const BREAKPOINT_CENTS = 35000;
  const MARGINAL_RATE = 0.015;
  const STEP_CENTS = 50;
  const COWBOY_HAT_TAG = 'cowboy hat';

  const VARIANTS = {
    200: 47382693544105,
    250: 47382693576873,
    300: 47382693609641,
    350: 47382693642409,
    400: 47382693675177,
    450: 47382693707945,
    500: 47382693740713,
    550: 47382693773481,
    600: 47382693806249,
    650: 47382693839017,
    700: 47382693871785,
    750: 47382693904553,
    800: 47382693937321,
    850: 47382693970089,
    900: 47382694002857,
    950: 47382694035625,
    1000: 47382694068393,
    1050: 47382694101161,
    1100: 47382694133929,
    1150: 47382694166697,
    1200: 47382694199465,
    1250: 47382694232233,
    1300: 47382694265001,
    1350: 47382694297769,
    1400: 47382694330537,
    1450: 47382694363305,
    1500: 47382694396073,
    1550: 47382694428841,
    1600: 47382694461609,
    1650: 47382694494377,
    1700: 47382694527145,
    1750: 47382694559913,
    1800: 47382694592681,
    1850: 47382694625449,
    1900: 47382694658217,
    1950: 47382694690985,
    2000: 47382694723753
  };

  let syncInProgress = false;
  let scheduledSync = null;
  let latestCart = null;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartUrl = () => `${root()}cart.js`;
  const cartAddUrl = () => `${root()}cart/add.js`;
  const cartChangeUrl = () => `${root()}cart/change.js`;
  const checkoutUrl = () => `${root()}checkout`;

  const isReturnItem = (item) => item && (item.sku === RETURN_SKU || item.handle === RETURN_HANDLE);

  async function productHasCowboyHatTag(item) {
    if (!item || !item.handle || isReturnItem(item)) return false;
    try {
      const response = await fetch(`${root()}products/${item.handle}.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) return false;
      const product = await response.json();
      const tags = Array.isArray(product.tags) ? product.tags : [];
      return tags.some((tag) => String(tag).trim().toLowerCase() === COWBOY_HAT_TAG);
    } catch (error) {
      return false;
    }
  }

  async function getMinimumFee(cart) {
    const merchandiseItems = cart.items.filter((item) => !isReturnItem(item));
    const tagChecks = await Promise.all(merchandiseItems.map(productHasCowboyHatTag));
    return tagChecks.some(Boolean) ? COWBOY_HAT_MIN_FEE_CENTS : DEFAULT_MIN_FEE_CENTS;
  }

  async function calculateFee(cart) {
    const merchandiseSubtotal = cart.items
      .filter((item) => !isReturnItem(item))
      .reduce((sum, item) => sum + item.final_line_price, 0);

    if (merchandiseSubtotal <= 0) return { merchandiseSubtotal: 0, feeCents: 0 };

    let rawFee;
    if (merchandiseSubtotal <= BREAKPOINT_CENTS) {
      rawFee = merchandiseSubtotal * BASE_RATE;
    } else {
      const baseFeeAtBreakpoint = BREAKPOINT_CENTS * BASE_RATE;
      rawFee = baseFeeAtBreakpoint + ((merchandiseSubtotal - BREAKPOINT_CENTS) * MARGINAL_RATE);
    }

    const minimumFee = await getMinimumFee(cart);
    const roundedUp = Math.ceil(rawFee / STEP_CENTS) * STEP_CENTS;
    const feeCents = Math.max(minimumFee, Math.min(MAX_FEE_CENTS, roundedUp));
    return { merchandiseSubtotal, feeCents };
  }

  const money = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  async function getCart() {
    const response = await fetch(cartUrl(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
    return response.json();
  }

  async function removeLine(item) {
    const response = await fetch(cartChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: item.key, quantity: 0 })
    });
    if (!response.ok) throw new Error(`Return add-on removal failed: ${response.status}`);
    return response.json();
  }

  async function addVariant(variantId) {
    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });
    if (!response.ok) throw new Error(`Return add-on add failed: ${response.status}`);
    return response.json();
  }

  function refreshThemeCart() {
    document.dispatchEvent(new CustomEvent('on:cart:change', {
      bubbles: true,
      cancelable: false
    }));
  }

  function hideNativeReturnLine() {
    document.querySelectorAll(`.product-${RETURN_HANDLE}`).forEach((line) => {
      const wrapper = line.closest('[data-merge-list-item]');
      if (wrapper) wrapper.style.display = 'none';
      else line.style.display = 'none';
    });
  }

  function makeAddonMarkup(cart, feeCents) {
    const currency = cart.currency || 'USD';
    return `
      <div class="ch-returns-addon" data-ch-returns-addon>
        <div class="ch-returns-addon__icon" aria-hidden="true">✓</div>
        <div class="ch-returns-addon__copy">
          <strong>Free Returns</strong>
          <span>Prepaid return shipping for this order</span>
        </div>
        <strong class="ch-returns-addon__price">${money(feeCents, currency)}</strong>
      </div>`;
  }

  function ensureStyles() {
    if (document.getElementById('ch-returns-styles')) return;
    const style = document.createElement('style');
    style.id = 'ch-returns-styles';
    style.textContent = `
      .ch-returns-addon{display:flex;align-items:center;gap:10px;margin:14px 0 10px;padding:10px 12px;border:1px solid rgba(0,0,0,.18);border-radius:4px;background:#fff;color:#111;text-align:left}
      .ch-returns-addon__icon{width:24px;height:24px;min-width:24px;border:1.5px solid currentColor;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;line-height:1}
      .ch-returns-addon__copy{display:flex;flex-direction:column;flex:1;line-height:1.25}
      .ch-returns-addon__copy strong{font-size:13px}
      .ch-returns-addon__copy span{font-size:11px;opacity:.72;margin-top:2px}
      .ch-returns-addon__price{font-size:13px;white-space:nowrap}
      [data-ch-add-returns]{background:#bf0e0e!important;color:#fff!important;border-color:#bf0e0e!important}
      [data-ch-checkout-without]{margin-top:8px;background:#000!important;color:#fff!important;border-color:#000!important}
      [data-ch-continue-shopping]{margin-top:8px;background:#fff!important;color:#111!important;border:1px solid #111!important}
      cart-form .additional-checkout-buttons, cart-form shopify-accelerated-checkout-cart, cart-form .continue-shopping{display:none!important}
    `;
    document.head.appendChild(style);
  }

  async function renderUI(cart) {
    if (!cart) return;
    latestCart = cart;
    ensureStyles();
    hideNativeReturnLine();

    const { feeCents } = await calculateFee(cart);
    if (!feeCents) return;

    const returnItem = cart.items.find(isReturnItem);
    const returnTotal = cart.items
      .filter(isReturnItem)
      .reduce((sum, item) => sum + item.final_line_price, 0);
    const merchandiseTotal = Math.max(0, cart.total_price - returnTotal);
    const containers = document.querySelectorAll('cart-form .checkout-buttons');

    containers.forEach((container) => {
      let addon = container.parentElement.querySelector('[data-ch-returns-addon]');
      if (!addon) {
        container.insertAdjacentHTML('beforebegin', makeAddonMarkup(cart, feeCents));
        addon = container.parentElement.querySelector('[data-ch-returns-addon]');
      }

      if (addon) {
        const price = addon.querySelector('.ch-returns-addon__price');
        if (price) price.textContent = money(feeCents, cart.currency || 'USD');
        addon.style.display = returnItem ? '' : 'none';
      }

      const primary = container.querySelector('a.btn, button.btn');
      if (!primary) return;
      primary.dataset.chAddReturns = 'true';
      delete primary.dataset.chCheckoutWithout;
      delete primary.dataset.chCheckoutWith;
      if (primary.tagName === 'BUTTON') primary.type = 'button';
      primary.textContent = `CHECKOUT WITH FREE RETURN SHIPPING • ${money(merchandiseTotal + feeCents, cart.currency || 'USD')}`;

      let checkoutWithout = container.querySelector('[data-ch-checkout-without]');
      if (!checkoutWithout) {
        checkoutWithout = document.createElement('button');
        checkoutWithout.type = 'button';
        checkoutWithout.className = `${primary.className} ch-returns-checkout-without`;
        checkoutWithout.dataset.chCheckoutWithout = 'true';
        primary.insertAdjacentElement('afterend', checkoutWithout);
      }
      checkoutWithout.textContent = `CHECKOUT WITHOUT FREE RETURN SHIPPING • ${money(merchandiseTotal, cart.currency || 'USD')}`;

      let continueShopping = container.querySelector('[data-ch-continue-shopping]');
      if (!continueShopping) {
        continueShopping = document.createElement('button');
        continueShopping.type = 'button';
        continueShopping.className = `${primary.className} ch-returns-continue-shopping`;
        continueShopping.dataset.chContinueShopping = 'true';
        checkoutWithout.insertAdjacentElement('afterend', continueShopping);
      }
      continueShopping.textContent = 'CONTINUE SHOPPING';
    });
  }

  async function syncReturnAddon({ refresh = true } = {}) {
    if (syncInProgress) return latestCart;
    syncInProgress = true;

    try {
      let cart = await getCart();
      const { feeCents } = await calculateFee(cart);
      const returnItems = cart.items.filter(isReturnItem);
      const hadReturnIntent = returnItems.length > 0;
      let changed = false;

      if (!feeCents) {
        for (const item of returnItems) {
          await removeLine(item);
          changed = true;
        }
      } else if (hadReturnIntent) {
        const targetVariantId = VARIANTS[feeCents];
        const correctItems = returnItems.filter((item) => Number(item.variant_id) === Number(targetVariantId));
        const keepItem = correctItems.find((item) => item.quantity === 1);

        for (const item of returnItems) {
          if (!keepItem || item.key !== keepItem.key) {
            await removeLine(item);
            changed = true;
          }
        }

        if (!keepItem) {
          await addVariant(targetVariantId);
          changed = true;
        }
      }

      if (changed) cart = await getCart();
      latestCart = cart;
      await renderUI(cart);
      if (changed && refresh) refreshThemeCart();
      return cart;
    } catch (error) {
      console.warn('Crazy House Free Returns:', error);
      return latestCart;
    } finally {
      syncInProgress = false;
    }
  }

  function scheduleSync(delay = 80) {
    clearTimeout(scheduledSync);
    scheduledSync = setTimeout(() => syncReturnAddon(), delay);
  }

  function cartTermsAccepted(button) {
    const cartForm = button && button.closest('cart-form');
    const terms = cartForm && cartForm.querySelector('terms-agreement');
    if (terms && !terms.querySelector('input:checked')) {
      theme.showQuickPopup(theme.strings.cartTermsConfirmation, terms.querySelector('[type="checkbox"]'));
      return false;
    }
    return true;
  }

  function beginAction(button) {
    if (!button || button.dataset.chBusy === 'true') return false;
    button.dataset.chBusy = 'true';
    button.setAttribute('aria-disabled', 'true');
    if ('disabled' in button) button.disabled = true;
    return true;
  }

  function resetAction(button) {
    if (!button) return;
    delete button.dataset.chBusy;
    button.removeAttribute('aria-disabled');
    if ('disabled' in button) button.disabled = false;
  }

  async function addFreeReturnsAndCheckout(evt) {
    evt.preventDefault();
    evt.stopPropagation();
    const button = evt.target.closest('[data-ch-add-returns]');
    if (!cartTermsAccepted(button) || !beginAction(button)) return;

    try {
      const cart = await getCart();
      const { feeCents } = await calculateFee(cart);
      const targetVariantId = VARIANTS[feeCents];
      if (!targetVariantId) throw new Error('No Return Reserve variant is available for this cart.');

      const returnItems = cart.items.filter(isReturnItem);
      for (const item of returnItems) await removeLine(item);
      await addVariant(targetVariantId);
      window.location.href = checkoutUrl();
    } catch (error) {
      resetAction(button);
      console.warn('Crazy House Free Returns:', error);
    }
  }

  async function checkoutWithoutReturns(evt) {
    evt.preventDefault();
    evt.stopPropagation();
    const button = evt.target.closest('[data-ch-checkout-without]');
    if (!cartTermsAccepted(button) || !beginAction(button)) return;

    try {
      const cart = await getCart();
      const returnItems = cart.items.filter(isReturnItem);
      for (const item of returnItems) await removeLine(item);
      window.location.href = checkoutUrl();
    } catch (error) {
      resetAction(button);
      console.warn('Crazy House Free Returns:', error);
    }
  }

  function continueShopping(evt) {
    evt.preventDefault();
    evt.stopPropagation();

    let productUrl = '';
    try {
      productUrl = window.sessionStorage.getItem('ch-last-viewed-product') || '';
    } catch (error) {
      productUrl = '';
    }

    if (productUrl.indexOf('/products/') === 0) {
      window.location.href = productUrl;
      return;
    }

    try {
      const referrer = new URL(document.referrer);
      if (referrer.origin === window.location.origin && referrer.pathname.indexOf('/products/') === 0) {
        window.location.href = `${referrer.pathname}${referrer.search}`;
        return;
      }
    } catch (error) {
      // No usable same-store product referrer.
    }

    window.location.href = `${root()}collections/all`;
  }

  document.addEventListener('click', (evt) => {
    if (evt.target.closest('[data-ch-add-returns]')) {
      addFreeReturnsAndCheckout(evt);
    } else if (evt.target.closest('[data-ch-checkout-without]')) {
      checkoutWithoutReturns(evt);
    } else if (evt.target.closest('[data-ch-continue-shopping]')) {
      continueShopping(evt);
    }
  });

  document.addEventListener('DOMContentLoaded', () => scheduleSync(150));
  document.addEventListener('on:cart:add', () => scheduleSync(50));
  document.addEventListener('on:cart:after-merge', () => {
    if (latestCart) renderUI(latestCart);
    scheduleSync(120);
  });
  document.addEventListener('on:cart-drawer:before-open', () => scheduleSync(0));
})();
